"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import { setLoggedIn as setReduxLoggedIn } from "@/redux/features/LoginModal/loginModalSlice";
import { validateInputData } from "@/utils/allValidations";
import {
  serverAuthOTPSend,
  serverAuthOTPVerify,
  serverCheckEmailExists,
  serverCheckPhoneExists,
  serverForgotPassword,
  serverLoginUser,
  serverRegisterGoogleUser,
  serverRegisterUser,
} from "./serverApiFile";
import { handleGoogleSignIn } from "./authService";
import {
  UseAuthFormOptions,
  AuthStep,
  AuthInputMode,
  GoogleAuthData,
} from "@/type/commonTypes";

export function useAuthForm({
  onSuccess,
  customRedirect,
  leadSource = "",
  buttonType = "login_page",
}: UseAuthFormOptions = {}) {
  const dispatch = useDispatch();
  const { isLoggedIn } = useSelector((state: RootState) => state.loginModal);

  // Form step & modes
  const [step, setStep] = useState<AuthStep>("identifier");
  const [inputMode, setInputMode] = useState<AuthInputMode>("phone");

  // WhatsApp consent checkbox (checked by default)
  const [whatsappConsent, setWhatsappConsent] = useState(true);

  // Input states
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [password, setPassword] = useState("");
  const [otp, setOtp] = useState("");
  const [otpOrderNo, setOtpOrderNo] = useState<string | number>("");
  const [isOtpVerifying, setIsOtpVerifying] = useState(false);
  const [resendTimer, setResendTimer] = useState(30);
  const [canResend, setCanResend] = useState(false);
  const [otpSuccessMessage, setOtpSuccessMessage] = useState("");
  const [forgotEmail, setForgotEmail] = useState("");
  const [forgotPasswordSuccessMessage, setForgotPasswordSuccessMessage] =
    useState("");
  const resendTimerRef = useRef<NodeJS.Timeout | null>(null);

  // Google sign in temp state
  const [googleData, setGoogleData] = useState<GoogleAuthData>({
    name: "",
    email: "",
    google_id: "",
  });

  // Status & error handling
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const startResendTimer = useCallback(() => {
    setCanResend(false);
    setResendTimer(30);
    if (resendTimerRef.current) clearInterval(resendTimerRef.current);
    resendTimerRef.current = setInterval(() => {
      setResendTimer((prev) => {
        if (prev <= 1) {
          if (resendTimerRef.current) clearInterval(resendTimerRef.current);
          setCanResend(true);
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  }, []);

  useEffect(() => {
    return () => {
      if (resendTimerRef.current) clearInterval(resendTimerRef.current);
    };
  }, []);

  const clearFieldError = useCallback((field: string) => {
    setErrors((prev) => {
      if (!prev[field]) return prev;
      const copy = { ...prev };
      delete copy[field];
      return copy;
    });
  }, []);

  const handlePhoneChange = useCallback(
    (val: string) => {
      clearFieldError("phone");
      const digits = val.replace(/\D/g, "").slice(0, 10);
      setPhone(digits);
    },
    [clearFieldError],
  );

  const handleEmailChange = useCallback(
    (val: string) => {
      clearFieldError("email");
      setEmail(val.toLowerCase().trim());
    },
    [clearFieldError],
  );

  const handleNameChange = useCallback(
    (val: string) => {
      clearFieldError("name");
      const formatted = val
        .replace(/\s{2,}/g, " ")
        .split(" ")
        .map((w) => (w.length > 0 ? w[0].toUpperCase() + w.slice(1) : ""))
        .join(" ");
      setName(formatted);
    },
    [clearFieldError],
  );

  const handlePasswordChange = useCallback(
    (val: string) => {
      clearFieldError("password");
      setPassword(val);
    },
    [clearFieldError],
  );

  const handleOtpChange = useCallback(
    (val: string) => {
      clearFieldError("otp");
      setOtpSuccessMessage("");
      const digits = val.replace(/\D/g, "").slice(0, 6);
      setOtp(digits);
    },
    [clearFieldError],
  );

  const handleForgotEmailChange = useCallback(
    (val: string) => {
      clearFieldError("forgotEmail");
      setForgotEmail(val.toLowerCase().trim());
    },
    [clearFieldError],
  );

  const completeAuth = useCallback(() => {
    dispatch(setReduxLoggedIn(true));
    if (onSuccess) {
      onSuccess();
    } else {
      window.location.href = customRedirect || "/";
    }
  }, [dispatch, onSuccess, customRedirect]);

  const handleResendOtp = useCallback(async () => {
    if (!canResend || loading || isOtpVerifying) return;
    setErrors({});
    setLoading(true);
    try {
      const res = await serverAuthOTPSend({ mobile: phone });
      if (res.status === 1) {
        if (res.orderNo) {
          setOtpOrderNo(res.orderNo);
        }
        setOtpSuccessMessage(res.message || `New OTP sent to +91 ${phone}`);
        startResendTimer();
      } else {
        setErrors({ otp: res.message || "Failed to resend OTP" });
      }
    } catch(err) {
      console.error(err,"Error : OTP Sending")
      setErrors({ otp: "Failed to resend OTP. Please try again." });
    } finally {
      setLoading(false);
    }
  }, [canResend, loading, isOtpVerifying, phone, startResendTimer]);

  // Google sign-in handler
  const handleGoogleBtn = useCallback(async () => {
    const clientId =
      "245951504565-ialuhbrtiq7o4q8mqjskn4ij39jp8blg.apps.googleusercontent.com";
    setLoading(true);
    setErrors({});

    try {
      const checkData = await handleGoogleSignIn(clientId);

      if (checkData?.existOrNot?.status === 1) {
        completeAuth();
      } else {
        setGoogleData({
          name: checkData.name,
          email: checkData.email,
          google_id: checkData.google_id,
        });
        setStep("google_phone");
      }
    } catch (err: unknown) {
      const message =
        err instanceof Error
          ? err.message
          : typeof err === "string"
            ? err
            : "Google login failed. Please try again.";
      setErrors({ [inputMode]: message });
    } finally {
      setLoading(false);
    }
  }, [completeAuth, inputMode]);

  // Step 1: Identifier Continue
  const handleIdentifierSubmit = useCallback(
    async (e?: React.FormEvent) => {
      if (e) e.preventDefault();
      setErrors({});

      if (inputMode === "phone") {
        if (!phone) {
          setErrors({ phone: "Please enter your mobile number" });
          return;
        }
        if (!validateInputData("phone", phone)) {
          setErrors({ phone: "Enter a valid 10-digit mobile number" });
          return;
        }

        setLoading(true);
        try {
          const exists = await serverCheckPhoneExists(phone);
          if (exists) {
            // Send OTP for sign in
            try {
              const otpRes = await serverAuthOTPSend({ mobile: phone });
              if (otpRes.status === 1) {
                if (otpRes.orderNo) {
                  setOtpOrderNo(otpRes.orderNo);
                }
                setOtpSuccessMessage(
                  otpRes.message || `OTP sent to +91 ${phone}`,
                );
                startResendTimer();
              } else {
                setOtpSuccessMessage("");
                setCanResend(true);
                setErrors({
                  otp:
                    otpRes.message || "Unable to send OTP. Please try again.",
                });
              }
            } catch (err: unknown) {
              console.error("OTP send error:", err);
              setOtpSuccessMessage("");
              setCanResend(true);
              setErrors({
                otp: "Unable to send OTP. Please try again.",
              });
            }
            setOtp("");
            setStep("signin");
          } else {
            setStep("signup_details");
          }
        } catch (err: unknown) {
          console.error(err);
          setErrors({ phone: "Unable to verify number. Please try again." });
        } finally {
          setLoading(false);
        }
      } else {
        if (!email) {
          setErrors({ email: "Please enter your email address" });
          return;
        }
        if (!validateInputData("email", email)) {
          setErrors({ email: "Enter a valid email address" });
          return;
        }

        setLoading(true);
        try {
          const exists = await serverCheckEmailExists(email);
          if (exists) {
            setStep("signin");
          } else {
            setStep("signup_details");
          }
        } catch (err: unknown) {
          console.error(err);
          setErrors({ email: "Unable to verify email. Please try again." });
        } finally {
          setLoading(false);
        }
      }
    },
    [inputMode, phone, email, startResendTimer],
  );

  // Step 2: Sign In Submit
  const handleSignInSubmit = useCallback(
    async (e?: React.FormEvent) => {
      if (e) e.preventDefault();
      setErrors({});

      if (inputMode === "phone") {
        if (!otp) {
          setErrors({ otp: "Please enter the OTP" });
          return;
        }
        if (otp.length < 6) {
          setErrors({ otp: "Please enter a valid 6-digit OTP" });
          return;
        }

        setLoading(true);
        setIsOtpVerifying(true);
        try {
          const verifyRes = await serverAuthOTPVerify({
            otp,
            mobile: phone,
            orderNo: otpOrderNo,
          });

          if (verifyRes.status !== 1) {
            setErrors({
              otp: verifyRes.message || "Invalid OTP. Please try again.",
            });
            setLoading(false);
            setIsOtpVerifying(false);
            return;
          }

          completeAuth();
        } catch (err: unknown) {
          console.error("OTP Verification Failed: ", err);
          setErrors({ otp: "OTP verification failed. Please try again." });
        } finally {
          setLoading(false);
          setIsOtpVerifying(false);
        }
      } else {
        if (!password.trim()) {
          setErrors({ password: "Enter your password to continue" });
          return;
        }

        setLoading(true);
        try {
          const loginRes = await serverLoginUser(email, "", password);

          if (loginRes.status === 1) {
            completeAuth();
          } else {
            setErrors({
              password:
                loginRes.msg || loginRes.message || "Invalid credentials.",
            });
          }
        } catch (err: unknown) {
          console.error(err);
          setErrors({
            password: "Login failed. Please check your credentials.",
          });
        } finally {
          setLoading(false);
        }
      }
    },
    [inputMode, otp, otpOrderNo, phone, password, email, completeAuth],
  );

  // Step 3A: Sign Up Details Continue
  const handleSignUpDetailsSubmit = useCallback(
    async (e?: React.FormEvent) => {
      if (e) e.preventDefault();
      const newErrors: Record<string, string> = {};

      if (!name.trim() || !validateInputData("name", name)) {
        newErrors.name = "Enter a valid full name";
      }

      if (inputMode === "email") {
        if (!phone || !validateInputData("phone", phone)) {
          newErrors.phone = "Enter a valid 10-digit mobile number";
        }
      } else {
        if (!email || !validateInputData("email", email)) {
          newErrors.email = "Enter a valid email address";
        }
      }

      if (!password || !validateInputData("password", password)) {
        newErrors.password = "Min 8 chars, 1 uppercase, 1 number";
      }

      if (Object.keys(newErrors).length > 0) {
        setErrors(newErrors);
        return;
      }

      setLoading(true);
      try {
        if (inputMode === "email") {
          const phoneExists = await serverCheckPhoneExists(phone);
          if (phoneExists) {
            setErrors({ phone: "This phone number is already registered." });
            setLoading(false);
            return;
          }
        } else {
          const emailExists = await serverCheckEmailExists(email);
          if (emailExists) {
            setErrors({ email: "This email is already registered." });
            setLoading(false);
            return;
          }
        }

        const otpRes = await serverAuthOTPSend({ mobile: phone });
        if (otpRes.status === 1) {
          if (otpRes.orderNo) {
            setOtpOrderNo(otpRes.orderNo);
          }
          setOtp("");
          setOtpSuccessMessage(otpRes.message || `OTP sent to +91 ${phone}`);
          startResendTimer();
          setStep("signup_otp");
        } else {
          setErrors({
            [inputMode === "email" ? "phone" : "email"]:
              otpRes.message ||
              "Failed to send verification OTP. Please try again.",
          });
        }
      } catch (err: unknown) {
        console.error(err);
        setErrors({
          [inputMode === "email" ? "phone" : "email"]:
            "Verification failed. Please try again.",
        });
      } finally {
        setLoading(false);
      }
    },
    [name, inputMode, phone, email, password, startResendTimer],
  );

  // Step 3B: Sign Up OTP Verification & Register
  const handleSignUpOtpSubmit = useCallback(
    async (e?: React.FormEvent) => {
      if (e) e.preventDefault();
      setErrors({});

      if (!otp) {
        setErrors({ otp: "Please enter the OTP" });
        return;
      }
      if (otp.length < 6) {
        setErrors({ otp: "Please enter a valid 6-digit OTP" });
        return;
      }

      setLoading(true);
      setIsOtpVerifying(true);
      try {
        const verifyRes = await serverAuthOTPVerify({
          otp,
          mobile: phone,
          orderNo: otpOrderNo,
        });

        if (verifyRes.status === 1) {
          const regRes = await serverRegisterUser(
            name,
            email,
            phone,
            password,
            leadSource,
            buttonType,
          );

          if (regRes.status === 1) {
            completeAuth();
          } else {
            setErrors({
              otp: regRes.msg || regRes.message || "Registration failed.",
            });
          }
        } else {
          setErrors({
            otp: verifyRes.message || "Invalid OTP. Please try again.",
          });
        }
      } catch (err: unknown) {
        console.error(err);
        setErrors({ otp: "Registration failed. Please try again." });
      } finally {
        setLoading(false);
        setIsOtpVerifying(false);
      }
    },
    [
      otp,
      phone,
      otpOrderNo,
      name,
      email,
      password,
      leadSource,
      buttonType,
      completeAuth,
    ],
  );

  // Step 4A: Google Phone Submit
  const handleGooglePhoneSubmit = useCallback(
    async (e?: React.FormEvent) => {
      if (e) e.preventDefault();
      setErrors({});

      if (!phone || !validateInputData("phone", phone)) {
        setErrors({ phone: "Enter a valid 10-digit mobile number" });
        return;
      }

      setLoading(true);
      try {
        const exists = await serverCheckPhoneExists(phone);
        if (exists) {
          setErrors({
            phone: "Number already exists. Please login or try another number.",
          });
          setLoading(false);
          return;
        }

        const otpRes = await serverAuthOTPSend({ mobile: phone });
        if (otpRes.status === 1) {
          if (otpRes.orderNo) {
            setOtpOrderNo(otpRes.orderNo);
          }
          setOtp("");
          setOtpSuccessMessage(otpRes.message || `OTP sent to +91 ${phone}`);
          startResendTimer();
          setStep("google_otp");
        } else {
          setErrors({
            phone: otpRes.message || "Failed to send OTP. Please try again.",
          });
        }
      } catch (err: unknown) {
        console.error(err);
        setErrors({ phone: "Something went wrong. Please try again." });
      } finally {
        setLoading(false);
      }
    },
    [phone, startResendTimer],
  );

  // Step 4B: Google OTP Submit & Complete
  const handleGoogleOtpSubmit = useCallback(
    async (e?: React.FormEvent) => {
      if (e) e.preventDefault();
      setErrors({});

      if (!otp) {
        setErrors({ otp: "Please enter the OTP" });
        return;
      }
      if (otp.length < 6) {
        setErrors({ otp: "Please enter a valid 6-digit OTP" });
        return;
      }

      setLoading(true);
      setIsOtpVerifying(true);
      try {
        const verifyRes = await serverAuthOTPVerify({
          otp,
          mobile: phone,
          orderNo: otpOrderNo,
        });

        if (verifyRes.status === 1) {
          const regRes = await serverRegisterGoogleUser({
            name: googleData.name,
            email: googleData.email,
            mobile: phone,
            google_id: googleData.google_id,
            leadSource,
            button_type: buttonType,
            whatsapp_status: whatsappConsent ? 1 : 0,
          });

          if (regRes.status === 1) {
            completeAuth();
          } else {
            setErrors({
              otp: regRes.msg || regRes.message || "Registration failed",
            });
          }
        } else {
          setErrors({
            otp: verifyRes.message || "Invalid OTP. Please try again.",
          });
        }
      } catch (err: unknown) {
        console.error(err);
        setErrors({ otp: "Something went wrong. Please try again." });
      } finally {
        setLoading(false);
        setIsOtpVerifying(false);
      }
    },
    [
      otp,
      phone,
      otpOrderNo,
      googleData,
      leadSource,
      buttonType,
      whatsappConsent,
      completeAuth,
    ],
  );

  // Step 5: Forgot Password Start
  const handleForgotPasswordStart = useCallback(
    async (e?: React.FormEvent) => {
      if (e) e.preventDefault();
      setErrors({});

      const targetEmail = forgotEmail.trim();
      if (!targetEmail || !validateInputData("email", targetEmail)) {
        setErrors({ forgotEmail: "Enter a valid email address" });
        return;
      }

      setLoading(true);
      try {
        const res = await serverForgotPassword(targetEmail);
        if (res && res.status === 1) {
          setForgotPasswordSuccessMessage(
            res.message || "We have sent you a password reset link on your email",
          );
          setStep("forgot_password_success");
        } else {
          setErrors({
            forgotEmail:
              res?.message || "Failed to send reset link. Please check your email and try again.",
          });
        }
      } catch (err: unknown) {
        console.error(err);
        setErrors({ forgotEmail: "Failed to send reset link. Please try again." });
      } finally {
        setLoading(false);
      }
    },
    [forgotEmail],
  );

  // Return to Login with Email option selected
  const handleReturnToLogin = useCallback(() => {
    setErrors({});
    setPassword("");
    setOtp("");
    setForgotPasswordSuccessMessage("");
    setInputMode("email");
    if (forgotEmail) {
      setEmail(forgotEmail);
    }
    setStep("signin");
  }, [forgotEmail]);

  const handleBack = useCallback(() => {
    setErrors({});
    setOtpSuccessMessage("");
    setOtp("");
    if (resendTimerRef.current) clearInterval(resendTimerRef.current);

    if (step === "signup_otp") {
      setStep("signup_details");
    } else if (step === "signup_details") {
      setStep("identifier");
    } else if (step === "google_otp") {
      setStep("google_phone");
    } else if (step === "google_phone") {
      setStep("identifier");
    } else if (step === "forgot_password_success") {
      setStep("forgot_password");
    } else if (step === "forgot_password") {
      setStep("signin");
      setInputMode("email");
      if (forgotEmail) setEmail(forgotEmail);
    } else {
      setStep("identifier");
    }
  }, [step, forgotEmail]);

  // Unified submit handler dispatching based on active step
  const handleStepSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();
      switch (step) {
        case "identifier":
          return handleIdentifierSubmit(e);
        case "signin":
          return handleSignInSubmit(e);
        case "signup_details":
          return handleSignUpDetailsSubmit(e);
        case "signup_otp":
          return handleSignUpOtpSubmit(e);
        case "google_phone":
          return handleGooglePhoneSubmit(e);
        case "google_otp":
          return handleGoogleOtpSubmit(e);
        case "forgot_password":
          return handleForgotPasswordStart(e);
        case "forgot_password_success":
          return handleReturnToLogin();
      }
    },
    [
      step,
      handleIdentifierSubmit,
      handleSignInSubmit,
      handleSignUpDetailsSubmit,
      handleSignUpOtpSubmit,
      handleGooglePhoneSubmit,
      handleGoogleOtpSubmit,
      handleForgotPasswordStart,
      handleReturnToLogin,
    ],
  );

  const getSubmitButtonText = useCallback(() => {
    switch (step) {
      case "identifier":
        return "Proceed";
      case "signin":
        return "Sign In";
      case "signup_details":
      case "google_phone":
        return "Continue";
      case "signup_otp":
        return "Verify & Create Account";
      case "google_otp":
        return "Verify & Complete Registration";
      case "forgot_password":
        return "Send Reset OTP";
      case "forgot_password_success":
        return "Return to login";
      default:
        return "Continue";
    }
  }, [step]);

  const resetForm = useCallback(() => {
    setStep("identifier");
    setInputMode("phone");
    setPhone("");
    setEmail("");
    setName("");
    setPassword("");
    setOtp("");
    setOtpOrderNo("");
    setIsOtpVerifying(false);
    setForgotEmail("");
    setForgotPasswordSuccessMessage("");
    setErrors({});
    setOtpSuccessMessage("");
    setGoogleData({ name: "", email: "", google_id: "" });
    if (resendTimerRef.current) clearInterval(resendTimerRef.current);
  }, []);

  return {
    // States
    step,
    setStep,
    inputMode,
    setInputMode,
    whatsappConsent,
    setWhatsappConsent,
    phone,
    setPhone,
    email,
    setEmail,
    name,
    setName,
    password,
    setPassword,
    otp,
    setOtp,
    otpOrderNo,
    setOtpOrderNo,
    isOtpVerifying,
    resendTimer,
    canResend,
    otpSuccessMessage,
    setOtpSuccessMessage,
    forgotEmail,
    setForgotEmail,
    forgotPasswordSuccessMessage,
    setForgotPasswordSuccessMessage,
    googleData,
    setGoogleData,
    loading,
    setLoading,
    errors,
    setErrors,
    isLoggedIn,

    // Input handlers
    clearFieldError,
    handlePhoneChange,
    handleEmailChange,
    handleNameChange,
    handlePasswordChange,
    handleOtpChange,
    handleForgotEmailChange,

    // Action handlers
    startResendTimer,
    handleResendOtp,
    completeAuth,
    handleGoogleBtn,
    handleIdentifierSubmit,
    handleSignInSubmit,
    handleSignUpDetailsSubmit,
    handleSignUpOtpSubmit,
    handleGooglePhoneSubmit,
    handleGoogleOtpSubmit,
    handleForgotPasswordStart,
    handleReturnToLogin,
    handleBack,
    handleStepSubmit,
    getSubmitButtonText,
    resetForm,
  };
}
